/**
 * Alpine handlers for the moderator appeals master-detail page.
 *
 *   - modAppealsPage: the page shell (mod/appeals/index.twig). Owns the split
 *     layout, list selection, deep-link URL sync, prev/next stepper through the
 *     current filtered queue, and post-decision advance. Loads the selected
 *     appeal's detail fragment from ModAppeals::detailPartialAction and injects
 *     it into the right pane (imperative innerHTML + Alpine.initTree, per
 *     CLAUDE.md — never x-html for AJAX panel content).
 *   - modAppealDecide: the decision panel inside the injected detail fragment
 *     (mod/appeals/_detail.twig). Posts approve/deny to /v1/appeals/{id}/decide
 *     via window.ApiClient. On success it dispatches `appeal-decided` and the
 *     shell advances the pane to the next appeal in the queue.
 *
 * IDs exceed Number.MAX_SAFE_INTEGER, so every id is handled as a string.
 */

function postFormUrlEncoded(endpoint, params) {
    const body = new URLSearchParams();
    Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value === null || value === undefined || value === '') {
            return;
        }
        body.append(key, String(value));
    });

    return window.ApiClient.request(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
    });
}

function confirmDecision(options) {
    return new Promise((resolve) => {
        if (!window.ConfirmModal || typeof window.ConfirmModal.show !== 'function') {
            resolve(window.confirm(options.message));
            return;
        }
        window.ConfirmModal.show({
            title: options.title,
            message: options.message,
            confirmText: options.confirmText,
            cancelText: 'Cancel',
            type: options.type || 'warning',
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
}

/**
 * Investigation panels loader. Each host (.js-investigation-panel) inside the
 * given root carries data-panel-url. Loaded HTML is injected via innerHTML and
 * Alpine/Lucide are re-initialized. Scoped to the injected detail fragment so
 * re-selecting an appeal loads that appeal's panels.
 */
function loadInvestigationPanels(root) {
    const container = root || document;
    const panelRoot = container.querySelector
        ? container.querySelector('#appeal-investigation-panels')
        : null;
    if (!panelRoot) {
        return;
    }

    const fetchOptions = {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
    };

    panelRoot.querySelectorAll('.js-investigation-panel').forEach((host) => {
        const url = host.dataset.panelUrl;
        const label = host.dataset.panelLabel || 'panel';
        if (!url) {
            return;
        }

        fetch(url, fetchOptions)
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Panel request failed');
                }
                return response.text();
            })
            .then((html) => {
                host.innerHTML = html;
                window.Alpine?.initTree(host);
                window.LucideIcons?.initialize(host);
            })
            .catch(() => {
                const errorBox = document.createElement('div');
                errorBox.className = 'bg-white dark:bg-gray-900 rounded-lg border border-red-200 dark:border-red-800 px-4 py-4 text-sm text-red-600 dark:text-red-400';
                errorBox.textContent = label + ' could not load.';
                while (host.firstChild) {
                    host.removeChild(host.firstChild);
                }
                host.appendChild(errorBox);
            });
    });
}

export function modAppealsPage(config = {}) {
    return {
        // Base path for the appeals queue (from LinksHelper), e.g. /app/mod/appeals.
        listPath: String(config.listPath || '/app/mod/appeals'),
        // Current filter scope (sort/status/user_id) as a query string, kept on
        // deep-link URLs so a shared link reproduces the moderator's queue.
        filterQuery: String(config.filterQuery || ''),
        // Status the queue actually rendered ('open'|'closed'|'all'). A decided
        // appeal only drops out of the visible list under the 'open' filter.
        resolvedStatus: String(config.resolvedStatus || 'open'),
        preselectAppealId: String(config.preselectAppealId || ''),

        selectedId: '',
        showDetailMobile: false,
        paneLoading: false,
        paneError: '',
        hasPrev: false,
        hasNext: false,

        // Newest pane fetch wins; late responses for a superseded selection are
        // dropped.
        fetchSeq: 0,

        init() {
            this.sizeShell();
            this.onResize = () => this.sizeShell();
            this.onPopState = () => this.syncFromUrl();
            window.addEventListener('resize', this.onResize);
            window.addEventListener('popstate', this.onPopState);

            if (this.preselectAppealId) {
                const target = this.preselectAppealId;
                this.preselectAppealId = '';
                // The browser is already at the deep-link URL — select without
                // pushing another history entry.
                this.selectAppeal(target, { push: false });
            }
        },

        destroy() {
            window.removeEventListener('resize', this.onResize);
            window.removeEventListener('popstate', this.onPopState);
        },

        // Fill the shell from its top to the viewport bottom (desktop only) so
        // the list and pane get independent scroll containers. Below lg the
        // panes stack and flow naturally.
        sizeShell() {
            const shell = this.$root;
            if (!shell) {
                return;
            }
            if (!window.matchMedia('(min-width: 1024px)').matches) {
                shell.style.height = '';
                return;
            }
            const top = shell.getBoundingClientRect().top;
            shell.style.height = Math.max(320, window.innerHeight - top) + 'px';
        },

        isSelected(id) {
            return this.selectedId !== '' && String(id) === this.selectedId;
        },

        // Ordered appeal ids currently rendered in the left queue, read live
        // from the DOM so the prev/next stepper and post-decision advance track
        // row removals.
        queueIds() {
            return Array.from(this.$root.querySelectorAll('.js-appeal-row'))
                .map((el) => el.dataset.appealId)
                .filter(Boolean);
        },

        updateStepper() {
            const ids = this.queueIds();
            const idx = ids.indexOf(this.selectedId);
            this.hasPrev = idx > 0;
            this.hasNext = idx > -1 && idx < ids.length - 1;
        },

        onRowClick(event, id) {
            // Let inner links/buttons (the username -> mod page link) behave
            // normally instead of selecting the row.
            if (event.target.closest('a, button')) {
                return;
            }
            if (event.metaKey || event.ctrlKey) {
                const url = event.currentTarget?.dataset?.appealUrl;
                if (url) {
                    window.open(url, '_blank', 'noopener');
                }
                return;
            }
            this.selectAppeal(id);
        },

        onRowAux(event) {
            if (event.button !== 1 || event.target.closest('a, button')) {
                return;
            }
            const url = event.currentTarget?.dataset?.appealUrl;
            if (url) {
                event.preventDefault();
                window.open(url, '_blank', 'noopener');
            }
        },

        async selectAppeal(id, options = {}) {
            const push = options.push !== false;
            const appealId = String(id);
            const seq = ++this.fetchSeq;

            this.selectedId = appealId;
            this.showDetailMobile = true;
            this.paneError = '';
            this.paneLoading = true;

            if (push) {
                const url = this.detailUrl(appealId);
                if (window.location.pathname + window.location.search !== url) {
                    window.history.pushState({ appealId }, '', url);
                }
            }

            const host = this.$refs.paneContent;
            try {
                const response = await fetch(this.paneUrl(appealId), {
                    credentials: 'same-origin',
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                });
                if (seq !== this.fetchSeq) {
                    return;
                }
                if (!response.ok) {
                    if (host) {
                        host.innerHTML = '';
                    }
                    this.paneError = response.status === 404
                        ? 'Appeal not found.'
                        : 'Could not load this appeal.';
                    return;
                }
                const html = await response.text();
                if (seq !== this.fetchSeq) {
                    return;
                }
                if (host) {
                    host.innerHTML = html;
                    if (window.Alpine) {
                        window.Alpine.initTree(host);
                    }
                    if (window.LucideIcons) {
                        window.LucideIcons.initialize(host);
                    }
                    loadInvestigationPanels(host);
                }
                if (this.$refs.pane) {
                    this.$refs.pane.scrollTop = 0;
                }
            } catch {
                if (seq !== this.fetchSeq) {
                    return;
                }
                if (host) {
                    host.innerHTML = '';
                }
                this.paneError = 'Could not load this appeal.';
            } finally {
                if (seq === this.fetchSeq) {
                    this.paneLoading = false;
                }
                this.$nextTick(() => this.updateStepper());
            }
        },

        prevAppeal() {
            const ids = this.queueIds();
            const idx = ids.indexOf(this.selectedId);
            if (idx > 0) {
                this.selectAppeal(ids[idx - 1]);
            }
        },

        nextAppeal() {
            const ids = this.queueIds();
            const idx = ids.indexOf(this.selectedId);
            if (idx > -1 && idx < ids.length - 1) {
                this.selectAppeal(ids[idx + 1]);
            }
        },

        // After a decision lands, advance to the next appeal in the current
        // filtered queue. Under the 'open' filter the decided appeal is no
        // longer open, so drop its row from the list first.
        handleDecided(detail) {
            const decidedId = String((detail && detail.appealId) || '');
            if (!decidedId) {
                return;
            }

            const ids = this.queueIds();
            const idx = ids.indexOf(decidedId);
            const nextId = idx > -1 && idx < ids.length - 1 ? ids[idx + 1] : '';

            if (this.resolvedStatus === 'open') {
                const row = this.$root.querySelector('.js-appeal-row[data-appeal-id="' + decidedId + '"]');
                if (row) {
                    row.remove();
                }
            }

            if (nextId) {
                this.selectAppeal(nextId);
            } else {
                this.clearSelection();
            }
            this.$nextTick(() => this.updateStepper());
        },

        clearSelection(options = {}) {
            const push = options.push !== false;
            this.fetchSeq += 1;
            this.selectedId = '';
            this.showDetailMobile = false;
            this.paneError = '';
            this.paneLoading = false;
            if (this.$refs.paneContent) {
                this.$refs.paneContent.innerHTML = '';
            }
            this.hasPrev = false;
            this.hasNext = false;
            if (push) {
                window.history.pushState({}, '', this.listUrl());
            }
        },

        backToList() {
            this.clearSelection();
        },

        // Reconcile selection with the address bar on browser back/forward.
        syncFromUrl() {
            const base = this.listPath.replace(/\/$/, '');
            const path = window.location.pathname.replace(/\/$/, '');
            if (path.startsWith(base + '/')) {
                const id = path.slice(base.length + 1).split('/')[0];
                if (/^\d+$/.test(id)) {
                    this.selectAppeal(id, { push: false });
                    return;
                }
            }
            this.clearSelection({ push: false });
        },

        detailUrl(id) {
            return this.listPath.replace(/\/$/, '') + '/' + id
                + (this.filterQuery ? '?' + this.filterQuery : '');
        },

        paneUrl(id) {
            return this.listPath.replace(/\/$/, '') + '/' + id + '/pane';
        },

        listUrl() {
            return this.listPath + (this.filterQuery ? '?' + this.filterQuery : '');
        },
    };
}

export function modAppealDecide(config = {}) {
    return {
        appealId: config.appealId,
        activeDecision: '',
        error: '',

        approve: {
            reasonCategory: 'restore-mistake',
            logMsg: '',
            grantsAutomodImmunity: false,
        },
        deny: {
            denialOutcomeCategory: 'guidelines',
            logMsg: '',
            subtype: '',
            cooldownDays: '',
        },

        selectDecision(decision) {
            this.activeDecision = decision;
            this.error = '';
        },

        // Signal the shell to remove this appeal from the queue and advance the
        // pane to the next one. Replaces the old full-page redirect so a
        // decision keeps the moderator in the master-detail flow.
        announceDecided() {
            this.$dispatch('appeal-decided', { appealId: String(this.appealId) });
        },

        async closeAsAlreadyActive() {
            this.error = '';
            const ok = await confirmDecision({
                title: 'Close appeal',
                message: 'The subject is already active. Closing the appeal will mark it approved and clear it from the queue. Continue?',
                confirmText: 'Close appeal',
                type: 'warning',
            });
            if (!ok) {
                return;
            }
            try {
                // Server takes the approveAlreadyRestored branch because the
                // subject has no deleted_users row, so no restore is performed
                // and no MOD_ACTION_RESTORE log entry is created — only the
                // appeal decision row. reason_category is required by the
                // endpoint; restore-user-requested fits the semantics (the user
                // is back; the appeal is moot).
                const response = await postFormUrlEncoded(`/appeals/${this.appealId}/decide`, {
                    decision: 'approved',
                    reason_category: 'restore-user-requested',
                    log_msg: 'Subject is already active, no restore action required',
                });

                if (response.ok) {
                    this.announceDecided();
                    return;
                }
                await this.handleErrorResponse(response);
            } catch (err) {
                this.error = (err && err.message) ? err.message : 'Network error';
            }
        },

        async approveAppeal() {
            this.error = '';
            if (this.activeDecision !== 'approved') {
                this.activeDecision = 'approved';
                return;
            }

            try {
                const response = await postFormUrlEncoded(`/appeals/${this.appealId}/decide`, {
                    decision: 'approved',
                    reason_category: this.approve.reasonCategory,
                    log_msg: this.approve.logMsg,
                    grants_automod_immunity: this.approve.grantsAutomodImmunity ? '1' : '0',
                });

                if (response.ok) {
                    this.announceDecided();
                    return;
                }
                await this.handleErrorResponse(response);
            } catch (err) {
                this.error = (err && err.message) ? err.message : 'Network error';
            }
        },

        async denyAppeal() {
            this.error = '';
            if (this.activeDecision !== 'denied') {
                this.activeDecision = 'denied';
                return;
            }
            if (!this.deny.denialOutcomeCategory) {
                this.error = 'Outcome category is required.';
                return;
            }
            const cooldownInput = String(this.deny.cooldownDays).trim();
            const cooldown = cooldownInput === '' ? null : Number(cooldownInput);
            if (
                cooldown !== null
                && (!/^\d+$/.test(cooldownInput) || !Number.isSafeInteger(cooldown) || cooldown < 1 || cooldown > 365)
            ) {
                this.error = 'Cooldown must be a whole number from 1-365 days.';
                return;
            }

            // A blank cooldown is the heaviest action: the user cannot
            // submit any further appeals until a moderator manually
            // reinstates them. Surface that explicitly as "danger" so the
            // mod has to acknowledge it. A set cooldown is a softer
            // reopening-window option and stays at "warning".
            const isFinal = cooldown === null;
            const message = isFinal
                ? 'Deny this appeal with no reopening window? The user will not be able to submit another appeal until you manually reinstate them.'
                : `Deny this appeal? The user can submit another appeal after ${cooldown} day${cooldown === 1 ? '' : 's'}.`;
            const ok = await confirmDecision({
                title: 'Deny appeal',
                message,
                confirmText: 'Deny appeal',
                type: isFinal ? 'danger' : 'warning',
            });
            if (!ok) {
                return;
            }

            try {
                const response = await postFormUrlEncoded(`/appeals/${this.appealId}/decide`, {
                    decision: 'denied',
                    denial_outcome_category: this.deny.denialOutcomeCategory,
                    log_msg: this.deny.logMsg,
                    subtype: this.deny.subtype,
                    cooldown_days: cooldown,
                });

                if (response.ok) {
                    this.announceDecided();
                    return;
                }
                await this.handleErrorResponse(response);
            } catch (err) {
                this.error = (err && err.message) ? err.message : 'Network error';
            }
        },

        async handleErrorResponse(response) {
            if (response.status === 409) {
                this.error = 'This appeal has already been decided. Refresh to see the latest state.';
                return;
            }
            let message = 'Request failed (' + response.status + ').';
            try {
                const body = await response.json();
                if (body && body.error && body.error.message) {
                    message = body.error.message;
                }
            } catch (_e) {
                // Body wasn't JSON; fall back to generic message.
            }
            this.error = message;
        },
    };
}
